import { logger } from '../../utils/logger';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AccountInfo, InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser';
import {
  EntraUserInfo,
  getMsalInstance,
  getEntraUserInfo,
  loginRequest,
} from '../../config/authConfig';
import { analyticsService } from '../../services/analytics.service';
import { isAuthRedirectInProgress, markAuthRedirectInProgress } from '../../utils/authRedirect';
import { getClientLogLevelHeader, LOG_LEVEL_HEADER } from '../../utils/log-level';

/**
 * Serializable Account Info
 *
 * Stores only the necessary serializable fields from MSAL AccountInfo
 * to avoid Redux non-serializable value warnings.
 */
export interface SerializableAccountInfo {
  homeAccountId: string;
  environment: string;
  tenantId: string;
  username: string;
  localAccountId: string;
  name?: string;
  idTokenClaims?: Record<string, unknown>;
}

/**
 * Helper function to serialize MSAL AccountInfo
 * Extracts only serializable fields to store in Redux
 */
const serializeAccount = (account: AccountInfo): SerializableAccountInfo => {
  return {
    homeAccountId: account.homeAccountId,
    environment: account.environment,
    tenantId: account.tenantId,
    username: account.username,
    localAccountId: account.localAccountId,
    name: account.name,
    idTokenClaims: account.idTokenClaims as Record<string, unknown>,
  };
};

/**
 * Auth Ready State
 *
 * Single source of truth for whether auth is fully complete.
 * Router should NOT render until authReadyState === 'ready'.
 */
export type AuthReadyState =
  | 'initializing' // Initial state, checking MSAL
  | 'authenticated_pending_roles' // User authenticated but roles not yet fetched
  | 'ready' // All auth complete, safe to render protected routes
  | 'unauthenticated'; // No user session

/**
 * Auth State Interface
 *
 * Updated to use Entra External ID (MSAL) authentication.
 * Tokens are managed by MSAL and not stored in Redux.
 */
export interface AuthState {
  // Entra External ID account info (serializable version)
  account: SerializableAccountInfo | null;
  userInfo: EntraUserInfo | null;

  // Legacy fields (for backward compatibility during migration)
  user: User | null;

  // Authentication state
  isAuthenticated: boolean;
  isInitialized: boolean;
  isLoading: boolean;
  rolesLoading: boolean; // True while fetching roles from access token
  rolesInitialized: boolean; // True after roles have been fetched (success or failure)
  authReadyState: AuthReadyState; // Single source of truth - router waits for 'ready'
  error: string | null;

  // Onboarding state
  onboardingRequired: boolean; // True if user needs to select SKU

  // Token expiration state
  tokenExpiredWithFailedRefresh: boolean; // True when token expired and refresh failed
  lastTokenRefreshAttempt: number | null; // Timestamp of last refresh attempt
  tokenExpiresOn: number | null; // Unix ms timestamp of when the current access token expires
}

/**
 * User Interface (legacy - for backward compatibility)
 */
export interface User {
  id: string;
  email: string;
  username: string;
  firstName: string;
  lastName: string;
  role: string;
  roles?: string[]; // Admin roles support
  createdAt: string;
  updatedAt: string;
}

/**
 * Initial State
 */
const initialState: AuthState = {
  account: null,
  userInfo: null,
  user: null,
  isAuthenticated: false,
  isInitialized: false,
  isLoading: false,
  rolesLoading: false,
  rolesInitialized: false,
  authReadyState: 'initializing',
  error: null,
  onboardingRequired: false,
  tokenExpiredWithFailedRefresh: false,
  lastTokenRefreshAttempt: null,
  tokenExpiresOn: null,
};

/**
 * Async Thunks
 */

/**
 * Validate existing session on app load
 *
 * Checks MSAL for existing authenticated accounts and initializes Redux state.
 * Returns account data to be set atomically in the fulfilled handler.
 */
export const validateSession = createAsyncThunk(
  'auth/validateSession',
  async (_, { rejectWithValue }) => {
    try {
      // Use static imports (getMsalInstance, getEntraUserInfo imported at top)
      const msalInstance = getMsalInstance();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        // User is already authenticated via MSAL
        const account = accounts[0];

        // Ensure account is valid
        if (!account) {
          return { authenticated: false, account: null, userInfo: null };
        }

        // CRITICAL: Probe whether the refresh token is still usable by attempting a
        // silent token acquisition BEFORE claiming the session is valid.
        //
        // When the app is opened after several days (refresh token expired), MSAL's
        // localStorage cache still contains a stale account entry.  Without this
        // probe the app reaches authReadyState='ready' and every dashboard API call
        // fires with an expired token → burst of 401 errors → delayed redirect.
        //
        // By catching InteractionRequiredAuthError here we clear the stale cache and
        // return authenticated:false immediately, so the loading screen transitions
        // directly to login with zero network errors visible to the user.
        //
        // Skip the probe on the /auth/callback page because handleRedirectPromise()
        // is still running at that point and a second acquireTokenSilent would race.
        if (window.location.pathname !== '/auth/callback') {
          try {
            await msalInstance.acquireTokenSilent({
              scopes: [
                import.meta.env.VITE_ENTRA_API_SCOPE || 'api://creatoros-api/user_impersonation',
              ],
              account,
            });
          } catch (tokenError) {
            if (tokenError instanceof InteractionRequiredAuthError) {
              // Refresh token expired or revoked – clear the stale cache so MSAL
              // doesn't keep returning the same invalid account on subsequent calls.
              logger.warn(
                '[validateSession] Refresh token expired/revoked – clearing stale MSAL cache'
              );
              msalInstance.clearCache({ account });
              return { authenticated: false, account: null, userInfo: null };
            }

            if (tokenError instanceof BrowserAuthError) {
              // BrowserAuthError occurs when MSAL has no valid refresh token and falls
              // back to a hidden iframe, which Entra External ID (CIAM) blocks with
              // X-Frame-Options: deny. The iframe times out or is rejected, MSAL throws
              // BrowserAuthError (e.g. "silent_sso_error", "silent_iframe_timeout").
              //
              // This is an auth failure, not a transient network error.
              // Clearing the stale cache here prevents the app from reaching
              // authReadyState='ready' with isAuthenticated=true, which would cause
              // the dashboard to render blank before loginRedirect fires.
              logger.warn(
                '[validateSession] Silent auth failed (BrowserAuthError) – clearing stale MSAL cache',
                { errorCode: (tokenError as BrowserAuthError).errorCode }
              );
              msalInstance.clearCache({ account });
              return { authenticated: false, account: null, userInfo: null };
            }

            // Non-auth errors (network glitch, transient MSAL issue) are non-fatal —
            // fall through and let the session proceed. The 401 interceptor and
            // fetchRolesFromAccessToken will handle any further token failures downstream.
            logger.warn(
              '[validateSession] Token probe failed with non-interaction error (proceeding):',
              { detail: tokenError instanceof Error ? tokenError.message : String(tokenError) }
            );
          }
        }

        const userInfo = getEntraUserInfo(account);

        // Return serialized account data to be set in fulfilled handler
        // Serialize account to avoid Redux non-serializable value warnings
        if (userInfo) {
          return { authenticated: true, account: serializeAccount(account), userInfo };
        }
      }

      // No authenticated account found
      return { authenticated: false, account: null, userInfo: null };
    } catch (error) {
      logger.error('[validateSession] Error checking MSAL accounts:', error);
      return rejectWithValue('Failed to validate session');
    }
  }
);

const GUID_LIKE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const getLoginHint = (account: AccountInfo): string | undefined => {
  const claimHint =
    (account.idTokenClaims as any)?.preferred_username ||
    (account.idTokenClaims as any)?.email ||
    (account.idTokenClaims as any)?.upn;

  const candidate = (claimHint || '').toString().trim();
  if (!candidate || !candidate.includes('@')) {
    return undefined;
  }

  const localPart = candidate.split('@')[0] || '';
  if (GUID_LIKE.test(localPart)) {
    return undefined;
  }

  return candidate;
};

const handleInteractionRequired = async (account: AccountInfo): Promise<void> => {
  // Skip loginRedirect on /auth/callback to prevent redirect loop during
  // initial token acquisition after federated login (Google → Entra CIAM)
  if (window.location.pathname === '/auth/callback') {
    logger.warn('[handleInteractionRequired] On auth callback page, skipping redirect');
    return;
  }

  if (isAuthRedirectInProgress()) {
    return;
  }

  markAuthRedirectInProgress();
  sessionStorage.setItem('auth_redirect_uri', window.location.pathname);

  const msalInstance = getMsalInstance();
  const loginHint = getLoginHint(account);

  await msalInstance.loginRedirect({
    ...loginRequest,
    loginHint,
  });
};

/**
 * Fetch user profile from backend
 *
 * Fetches complete user data including roles from the backend API.
 * This should be called after authentication to get role information.
 */
export const fetchUserProfile = createAsyncThunk(
  'auth/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const msalInstance = getMsalInstance();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        return rejectWithValue('No authenticated account');
      }

      const account = accounts[0];
      if (!account) {
        return rejectWithValue('No authenticated account');
      }

      let tokenResponse;
      try {
        // Get access token for backend API
        tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: [
            import.meta.env.VITE_ENTRA_API_SCOPE || 'api://creatoros-api/user_impersonation',
          ],
          account: account,
        });
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await handleInteractionRequired(account);
          return rejectWithValue('interaction_required');
        }
        throw error;
      }

      // Fetch user profile from backend
      const correlationId = analyticsService.getOperationId();
      const headers: Record<string, string> = {
        Authorization: `Bearer ${tokenResponse.accessToken}`,
        'Content-Type': 'application/json',
        'X-Correlation-ID': correlationId,
      };
      const logLevelHeader = getClientLogLevelHeader();
      if (logLevelHeader) headers[LOG_LEVEL_HEADER] = logLevelHeader;

      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/users/me`, {
        headers,
      });

      if (!response.ok) {
        // Handle 402 - onboarding required
        if (response.status === 402) {
          const errorData = await response.json();
          return {
            onboardingRequired: true,
            redirectTo: errorData?.details?.redirectTo || '/onboarding/select-plan',
          };
        }
        throw new Error(`Failed to fetch user profile: ${response.statusText}`);
      }

      const userData = await response.json();
      // Check if onboarding is required from the user data
      const onboardingRequired = userData?.data?.onboardingStatus === 'pending_sku_selection';
      return { ...userData, onboardingRequired };
    } catch (error) {
      logger.error('[fetchUserProfile] Error fetching user profile:', error);
      return rejectWithValue('Failed to fetch user profile');
    }
  }
);

/**
 * CRITICAL FIX FOR ENTRA EXTERNAL ID (CIAM):
 * Extract roles from access token
 *
 * Entra External ID does NOT include app roles in ID tokens.
 * App roles are only included in access tokens when requesting API scopes.
 * This thunk acquires an access token and extracts roles from it.
 *
 * @see https://learn.microsoft.com/en-us/entra/external-id/customers/concept-supported-features-customers
 */
export const fetchRolesFromAccessToken = createAsyncThunk(
  'auth/fetchRolesFromAccessToken',
  async (_, { rejectWithValue }) => {
    try {
      const msalInstance = getMsalInstance();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length === 0) {
        return rejectWithValue('No authenticated account');
      }

      const account = accounts[0];
      if (!account) {
        return rejectWithValue('No authenticated account');
      }

      let tokenResponse;
      try {
        // Get access token for the API (this will include roles)
        tokenResponse = await msalInstance.acquireTokenSilent({
          scopes: [
            import.meta.env.VITE_ENTRA_API_SCOPE || 'api://creatoros-api/user_impersonation',
          ],
          account: account,
        });
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await handleInteractionRequired(account);
          return rejectWithValue('interaction_required');
        }
        if (error instanceof BrowserAuthError) {
          // BrowserAuthError (e.g. iframe blocked/timed out by Entra External ID's
          // X-Frame-Options: deny) means there is no usable refresh token.
          // Treat it as an auth failure — the reducer will set isAuthenticated=false
          // and authReadyState='unauthenticated', preventing a blank dashboard render.
          logger.warn('[fetchRolesFromAccessToken] BrowserAuthError during token acquisition:', {
            errorCode: (error as BrowserAuthError).errorCode,
          });
          return rejectWithValue('interaction_required');
        }
        throw error;
      }

      if (!tokenResponse?.accessToken) {
        throw new Error('No access token received');
      }

      // Decode the access token to extract roles
      // JWT structure: header.payload.signature
      const parts = tokenResponse.accessToken.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid JWT token format');
      }

      const base64Url = parts[1];
      if (!base64Url) {
        throw new Error('Invalid JWT token format - missing payload');
      }
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const accessTokenClaims = JSON.parse(jsonPayload);

      // Extract roles from access token
      let roles: string[] = ['user']; // Default role
      if (accessTokenClaims.roles && Array.isArray(accessTokenClaims.roles)) {
        roles = accessTokenClaims.roles;
      } else if (accessTokenClaims.roles && typeof accessTokenClaims.roles === 'string') {
        roles = [accessTokenClaims.roles];
      }
      return { roles };
    } catch (error) {
      logger.error('[authSlice] ❌ Failed to extract roles from access token:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

/**
 * Auth Slice
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    /**
     * Set Entra External ID account after successful authentication
     */
    setEntraAccount: (
      state,
      action: PayloadAction<{
        account: SerializableAccountInfo;
        userInfo: EntraUserInfo;
      }>
    ) => {
      // Account is already serialized before dispatch to avoid Redux non-serializable value warnings
      state.account = action.payload.account;
      state.userInfo = action.payload.userInfo;
      state.isAuthenticated = true;
      state.error = null;

      // CRITICAL: Set rolesLoading=true and authReadyState to prevent race condition
      state.rolesLoading = true;
      state.authReadyState = 'authenticated_pending_roles';

      // Map to legacy user object for compatibility
      state.user = {
        id: action.payload.userInfo.userId,
        email: action.payload.userInfo.email,
        username: action.payload.userInfo.username,
        firstName: action.payload.userInfo.name.split(' ')[0] || '',
        lastName: action.payload.userInfo.name.split(' ').slice(1).join(' ') || '',
        role: 'user', // Legacy field for backwards compatibility
        roles: action.payload.userInfo.roles, // Roles from JWT token (may be empty)
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
    },

    /**
     * Clear Entra account on logout
     */
    clearEntraAccount: (state) => {
      state.account = null;
      state.userInfo = null;
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.onboardingRequired = false;
      state.tokenExpiredWithFailedRefresh = false;
      state.lastTokenRefreshAttempt = null;
      state.tokenExpiresOn = null;
      state.rolesLoading = false;
      state.rolesInitialized = false;
      state.authReadyState = 'unauthenticated';
    },

    /**
     * Set user credentials after login (legacy - for backward compatibility)
     * @deprecated Use setEntraAccount instead
     */
    setCredentials: (
      state,
      action: PayloadAction<{
        user: User;
        token: string;
        refreshToken?: string;
      }>
    ) => {
      state.user = action.payload.user;
      state.isAuthenticated = true;
      state.error = null;
    },

    /**
     * Clear credentials on logout (legacy - for backward compatibility)
     * @deprecated Use clearEntraAccount instead
     */
    clearCredentials: (state) => {
      state.account = null;
      state.userInfo = null;
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },

    /**
     * Update user data
     */
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },

    /**
     * Set error
     */
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Set token expiration flag when refresh fails
     */
    setTokenExpiredWithFailedRefresh: (state, action: PayloadAction<boolean>) => {
      state.tokenExpiredWithFailedRefresh = action.payload;
    },

    /**
     * Set last token refresh attempt timestamp
     */
    setLastTokenRefreshAttempt: (state, action: PayloadAction<number | null>) => {
      state.lastTokenRefreshAttempt = action.payload;
    },

    /**
     * Store the expiry timestamp (Unix ms) of the latest successfully acquired
     * access token. Updated by useTokenRefresh on every successful silent
     * acquireTokenSilent so that the API request interceptor can do a cheap
     * synchronous pre-flight check without an async MSAL call.
     */
    setTokenExpiresOn: (state, action: PayloadAction<number | null>) => {
      state.tokenExpiresOn = action.payload;
    },

    /**
     * Set onboarding required flag
     */
    setOnboardingRequired: (state, action: PayloadAction<boolean>) => {
      state.onboardingRequired = action.payload;
    },

    /**
     * Complete onboarding (SKU selected)
     */
    completeOnboarding: (state) => {
      state.onboardingRequired = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Validate session
      .addCase(validateSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(validateSession.fulfilled, (state, action) => {
        // Atomically set both isInitialized and isAuthenticated to prevent race conditions
        state.isInitialized = true;
        state.isLoading = false;

        if (action.payload.authenticated && action.payload.account && action.payload.userInfo) {
          // Set authentication state atomically - account is already serialized from thunk
          state.account = action.payload.account;
          state.userInfo = action.payload.userInfo;
          state.isAuthenticated = true;
          state.error = null;

          // CRITICAL: Set authReadyState to wait for roles
          state.rolesLoading = true;
          state.authReadyState = 'authenticated_pending_roles';

          // Map to legacy user object for compatibility
          state.user = {
            id: action.payload.userInfo.userId,
            email: action.payload.userInfo.email,
            username: action.payload.userInfo.username,
            firstName: action.payload.userInfo.name.split(' ')[0] || '',
            lastName: action.payload.userInfo.name.split(' ').slice(1).join(' ') || '',
            role: 'user', // Legacy field for backwards compatibility
            roles: action.payload.userInfo.roles, // Roles from JWT token (may be empty)
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
        } else {
          // No authenticated account
          state.isAuthenticated = false;
          state.authReadyState = 'unauthenticated';
        }
      })
      .addCase(validateSession.rejected, (state) => {
        state.isInitialized = true;
        state.isLoading = false;
        state.isAuthenticated = false;
        state.authReadyState = 'unauthenticated';
      })
      // Fetch user profile
      .addCase(fetchUserProfile.pending, (_state) => {
        // Don't set isLoading to avoid loading spinner during background fetch
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        // Check if onboarding is required
        if (action.payload?.onboardingRequired) {
          state.onboardingRequired = true;
          return;
        }

        state.onboardingRequired = false;

        // Update user with data from backend (including roles)
        if (state.user && action.payload) {
          state.user = {
            ...state.user,
            ...action.payload,
            // Ensure roles are set from backend
            roles: action.payload.roles || [],
          };
        }
      })
      .addCase(fetchUserProfile.rejected, (_state, action) => {
        if (action.payload !== 'interaction_required') {
          logger.error('[authSlice] Failed to fetch user profile:', action.payload);
        }
        // Don't clear authentication state on profile fetch failure
      })
      // Fetch roles from access token (Entra External ID fix)
      .addCase(fetchRolesFromAccessToken.pending, (state) => {
        state.rolesLoading = true;
      })
      .addCase(fetchRolesFromAccessToken.fulfilled, (state, action) => {
        state.rolesLoading = false;
        state.rolesInitialized = true;
        state.authReadyState = 'ready'; // Auth is now fully complete
        // Update user with roles from access token
        if (state.user && action.payload.roles) {
          // CRITICAL: Create new user object to trigger React re-render
          state.user = {
            ...state.user,
            roles: action.payload.roles,
          };
        }
      })
      .addCase(fetchRolesFromAccessToken.rejected, (state, action) => {
        state.rolesLoading = false;
        state.rolesInitialized = true;
        if (action.payload === 'interaction_required') {
          // Can't get API access tokens - treat as unauthenticated
          // This prevents navigating to protected routes that will just trigger loginRedirect
          state.authReadyState = 'unauthenticated';
          state.isAuthenticated = false;
          state.account = null;
          state.userInfo = null;
          state.user = null;
        } else {
          state.authReadyState = 'ready'; // Auth is ready even on failure (use default roles)
          logger.error('[authSlice] Failed to fetch roles from access token:', action.payload);
        }
        // Don't fail authentication for non-interaction errors, just use default role
      });
  },
});

export const {
  setEntraAccount,
  clearEntraAccount,
  setCredentials,
  clearCredentials,
  updateUser,
  setError,
  clearError,
  setTokenExpiredWithFailedRefresh,
  setLastTokenRefreshAttempt,
  setTokenExpiresOn,
  setOnboardingRequired,
  completeOnboarding,
} = authSlice.actions;

// Export serializeAccount helper for use when dispatching setEntraAccount
export { serializeAccount };

export default authSlice.reducer;
